// src/components/coordinateGrid/CoordinateTable.tsx
/**
 * CoordinateTable Component
 * 
 * Draggable table showing x/y value pairs linked to equations.
 * Phase 4: Coordinate Tables
 * 
 * Based on existing RatioTableOverlay pattern.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GripVertical,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  X,
  ScatterChart,
} from 'lucide-react';
import { RootState } from '../../types/redux';
import { CoordinateTable as CoordinateTableType, Point2D } from '../../types/coordinateGrid';
import {
  setTablePosition,
  addXValue,
  removeXValue,
  setXValues,
  toggleYValuesVisibility,
  toggleEquationVisibility,
  toggleTableCollapsed,
  deleteTable,
  updateTable,
} from '../../store/slices/coordinateTablesSlice';
import { selectAllEquations } from '../../store/slices/equationsSlice';
import { addPoint } from '../../store/slices/plotPointsSlice';
import { evaluateY, parseEquation } from '../../lib/coordinateGrid/equationParser';

// =============================================================================
// TYPES
// =============================================================================

export interface CoordinateTableProps {
  /** The table data */
  table: CoordinateTableType;
  /** Container bounds for constraining drag */
  containerRef: React.RefObject<HTMLElement>;
  /** Dark mode */
  isDark?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export const CoordinateTable: React.FC<CoordinateTableProps> = ({
  table,
  containerRef,
  isDark = false,
}) => {
  const dispatch = useDispatch();
  const equations = useSelector(selectAllEquations);
  const linkedEquation = table.equationId
    ? equations.find((eq) => eq.id === table.equationId)
    : null;

  const tableRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState(table.position);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  // Update position when table position changes externally
  useEffect(() => {
    setPosition(table.position);
  }, [table.position]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (!tableRef.current) return;

      const rect = tableRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    },
    []
  );

  // Handle drag move
  useEffect(() => {
    if (!isDragging || !containerRef.current || !tableRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const container = containerRef.current!.getBoundingClientRect();
      const tableRect = tableRef.current!.getBoundingClientRect();

      let newX = e.clientX - container.left - dragOffset.x;
      let newY = e.clientY - container.top - dragOffset.y;

      // Constrain to container bounds
      newX = Math.max(0, Math.min(newX, container.width - tableRect.width));
      newY = Math.max(0, Math.min(newY, container.height - tableRect.height));

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dispatch(setTablePosition({ id: table.id, position }));
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, position, dispatch, table.id, containerRef]);

  // Calculate y values from equation
  const yValues = useMemo(() => {
    if (!linkedEquation || !table.showYValues) {
      return table.xValues.map(() => null as number | null);
    }

    const parsed = parseEquation(linkedEquation.expression);
    if (!parsed.isValid) {
      return table.xValues.map(() => null as number | null);
    }

    return table.xValues.map((x) => evaluateY(parsed, x));
  }, [linkedEquation, table.xValues, table.showYValues]);

  // Handle adding x value
  const handleAddXValue = useCallback(() => {
    const newValue = prompt('Enter x value:');
    if (newValue !== null) {
      const numValue = parseFloat(newValue);
      if (!isNaN(numValue)) {
        dispatch(addXValue({ tableId: table.id, value: numValue }));
      }
    }
  }, [dispatch, table.id]);

  // Handle quick range generator
  const handleGenerateRange = useCallback(() => {
    const startStr = prompt('Start x value:');
    const endStr = prompt('End x value:');
    const stepStr = prompt('Step size:', '1');

    if (startStr !== null && endStr !== null && stepStr !== null) {
      const start = parseFloat(startStr);
      const end = parseFloat(endStr);
      const step = parseFloat(stepStr);

      if (!isNaN(start) && !isNaN(end) && !isNaN(step) && step > 0) {
        const values: number[] = [];
        if (start <= end) {
          for (let x = start; x <= end; x += step) {
            values.push(x);
          }
        } else {
          for (let x = start; x >= end; x -= step) {
            values.push(x);
          }
        }
        dispatch(setXValues({ tableId: table.id, values }));
      }
    }
  }, [dispatch, table.id]);

  // Handle editing x value
  const handleStartEdit = useCallback(
    (index: number) => {
      setEditingIndex(index);
      setEditValue(table.xValues[index].toString());
    },
    [table.xValues]
  );

  const handleSaveEdit = useCallback(() => {
    if (editingIndex === null) return;

    const numValue = parseFloat(editValue);
    if (!isNaN(numValue)) {
      const newValues = [...table.xValues];
      newValues[editingIndex] = numValue;
      dispatch(setXValues({ tableId: table.id, values: newValues }));
    }
    setEditingIndex(null);
    setEditValue('');
  }, [editingIndex, editValue, table.xValues, dispatch, table.id]);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditValue('');
  }, []);

  // Handle plot all points
  const handlePlotAll = useCallback(() => {
    if (!linkedEquation) return;

    const parsed = parseEquation(linkedEquation.expression);
    if (!parsed.isValid) return;

    table.xValues.forEach((x) => {
      const y = evaluateY(parsed, x);
      if (y !== null && isFinite(y)) {
        dispatch(addPoint({ x, y }));
      }
    });
  }, [linkedEquation, table.xValues, dispatch]);

  // Format number for display
  const formatNumber = (value: number | null): string => {
    if (value === null) return '?';
    // Round to 2 decimal places for display
    const rounded = Math.round(value * 100) / 100;
    return rounded.toString();
  };

  if (table.isCollapsed) {
    return (
      <div
        ref={tableRef}
        className={`absolute z-50 rounded-lg shadow-lg border ${
          isDragging ? 'cursor-move' : ''
        } ${
          isDark
            ? 'bg-gray-800 border-gray-600'
            : 'bg-white border-gray-300'
        }`}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        <div
          className={`flex items-center justify-between px-3 py-2 cursor-move ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
          onMouseDown={handleDragStart}
        >
          <div className="flex items-center gap-2">
            <GripVertical size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Coordinate Table
            </span>
          </div>
          <button
            onClick={() => dispatch(toggleTableCollapsed(table.id))}
            className={`p-1 rounded transition-colors ${
              isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
            }`}
          >
            <ChevronDown size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={tableRef}
      className={`absolute z-50 rounded-lg shadow-2xl border-2 ${
        isDragging ? 'cursor-move' : ''
      } ${
        isDark
          ? 'bg-gray-800 border-gray-600'
          : 'bg-white border-gray-300'
      }`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        minWidth: '300px',
      }}
    >
      {/* Header with drag handle */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-b cursor-move ${
          isDark ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
        }`}
        onMouseDown={handleDragStart}
      >
        <div className="flex items-center gap-2">
          <GripVertical size={16} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
          <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Coordinate Table
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => dispatch(toggleTableCollapsed(table.id))}
            className={`p-1 rounded transition-colors ${
              isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
            }`}
            title="Collapse"
          >
            <ChevronUp size={14} />
          </button>
          <button
            onClick={() => dispatch(deleteTable(table.id))}
            className={`p-1 rounded transition-colors ${
              isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
            }`}
            title="Delete table"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Table content */}
      <div className="overflow-x-auto">
        {/* Equation display */}
        {table.showEquation && linkedEquation && (
          <div
            className={`px-3 py-2 text-sm border-b ${
              isDark ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-700'
            }`}
          >
            <span className="font-mono">{linkedEquation.expression}</span>
          </div>
        )}

        {/* Table */}
        <table className="w-full">
          <thead>
            <tr className={isDark ? 'bg-gray-750' : 'bg-gray-100'}>
              <th
                className={`px-3 py-2 text-left text-xs font-medium ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                x
              </th>
              {table.xValues.map((_, idx) => (
                <th
                  key={idx}
                  className={`px-2 py-2 text-center text-xs ${
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}
                  style={{ width: `${table.columnWidth}px` }}
                >
                  <button
                    onClick={() => dispatch(removeXValue({ tableId: table.id, index: idx }))}
                    className={`p-0.5 rounded hover:bg-red-500/20 transition-colors`}
                    title="Remove column"
                  >
                    <Trash2 size={12} className="text-red-500" />
                  </button>
                </th>
              ))}
              <th className={`px-2 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <button
                  onClick={handleAddXValue}
                  className={`p-1 rounded transition-colors ${
                    isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                  }`}
                  title="Add x value"
                >
                  <Plus size={14} />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* X values row */}
            <tr>
              <td
                className={`px-3 py-2 text-sm font-medium border-r ${
                  isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-200'
                }`}
              >
                x
              </td>
              {table.xValues.map((x, idx) => (
                <td
                  key={idx}
                  className={`px-2 py-2 text-center text-sm border-r ${
                    isDark
                      ? 'text-gray-300 border-gray-700'
                      : 'text-gray-700 border-gray-200'
                  }`}
                  style={{ width: `${table.columnWidth}px` }}
                >
                  {editingIndex === idx ? (
                    <input
                      type="number"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleSaveEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveEdit();
                        } else if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                      autoFocus
                      className={`w-full text-center border rounded px-1 ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  ) : (
                    <button
                      onClick={() => handleStartEdit(idx)}
                      className={`w-full hover:bg-gray-100 dark:hover:bg-gray-700 rounded px-1 ${
                        isDark ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      {formatNumber(x)}
                    </button>
                  )}
                </td>
              ))}
              <td></td>
            </tr>

            {/* Y values row */}
            {table.showYValues && (
              <tr>
                <td
                  className={`px-3 py-2 text-sm font-medium border-r ${
                    isDark ? 'text-gray-300 border-gray-700' : 'text-gray-700 border-gray-200'
                  }`}
                >
                  y
                </td>
                {yValues.map((y, idx) => (
                  <td
                    key={idx}
                    className={`px-2 py-2 text-center text-sm border-r ${
                      isDark
                        ? 'text-gray-300 border-gray-700'
                        : 'text-gray-700 border-gray-200'
                    }`}
                    style={{ width: `${table.columnWidth}px` }}
                  >
                    {formatNumber(y)}
                  </td>
                ))}
                <td></td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Actions */}
        <div
          className={`flex items-center justify-between px-3 py-2 border-t gap-2 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center gap-2">
            <button
              onClick={() => dispatch(toggleYValuesVisibility(table.id))}
              className={`p-1 rounded transition-colors ${
                isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
              }`}
              title={table.showYValues ? 'Hide y values' : 'Show y values'}
            >
              {table.showYValues ? (
                <Eye size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              ) : (
                <EyeOff size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
              )}
            </button>
            {linkedEquation && (
              <button
                onClick={() => dispatch(toggleEquationVisibility(table.id))}
                className={`p-1 rounded transition-colors ${
                  isDark ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
                }`}
                title={table.showEquation ? 'Hide equation' : 'Show equation'}
              >
                {table.showEquation ? (
                  <Eye size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                ) : (
                  <EyeOff size={14} className={isDark ? 'text-gray-400' : 'text-gray-500'} />
                )}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleGenerateRange}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                isDark
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
              title="Generate x-value range"
            >
              Range
            </button>
            {linkedEquation && (
              <button
                onClick={handlePlotAll}
                className={`px-2 py-1 text-xs rounded transition-colors flex items-center gap-1 ${
                  isDark
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
                title="Plot all points on graph"
              >
                <ScatterChart size={12} />
                Plot All
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
